/** Web Push in the browser: the service worker, permission, subscription and the iPhone "Home Screen" hint. */
import { api } from '../api';

export const PUSH_KEY = ['notifications', 'push'] as const;

/** GET /api/me/push */
export type PushSettings = {
  enabled: boolean;
  publicKey: string | null;
  devices: Array<{ id: string; device: string; createdAt: string; lastUsedAt: string | null; endpoint: string }>;
  types: Array<{ id: string; label: string; enabled: boolean }>;
};

/** Whether this browser can receive push at all (service worker, Push API, notifications). */
export function pushSupported(w: Window = window): boolean {
  return 'serviceWorker' in w.navigator && 'PushManager' in w && 'Notification' in w;
}

/** iPhone/iPad (also an iPad that reports itself as a Mac). */
export function isIos(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): boolean {
  return /iPhone|iPad|iPod/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1);
}

/** Opened from the Home Screen (standalone), not in a Safari tab. */
export function isStandalone(w: Window = window): boolean {
  return (w.navigator as Navigator & { standalone?: boolean }).standalone === true || w.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** On an iPhone push works only for the app added to the Home Screen (iOS 16.4+): then show the hint. */
export function needsHomeScreen(w: Window = window): boolean {
  return isIos(w.navigator) && !isStandalone(w);
}

/** The VAPID public key (base64url) as the bytes `subscribe` takes. */
export function keyBytes(base64url: string): Uint8Array {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Registers the service worker once (the app calls it at start); quietly does nothing where unsupported. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => {
    /* e.g. a private window: push is simply not offered */
  });
}

/** A click on a push while the app is open: the service worker asks the app to go to the link. */
export function listenForPushClicks(go: (link: string) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => {};
  const onMessage = (e: MessageEvent) => {
    const d = e.data as { type?: string; link?: unknown } | null;
    if (d?.type === 'mek-push-open' && typeof d.link === 'string' && d.link.startsWith('/') && !d.link.startsWith('//')) go(d.link);
  };
  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
}

/** This browser's push subscription, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export type EnableResult = { ok: true; subscription: PushSubscriptionJSON } | { ok: false; reason: 'unsupported' | 'denied' | 'failed' };

/** Asks for permission and subscribes this browser with the server's key. */
export async function subscribeThisDevice(publicKey: string): Promise<EnableResult> {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };
  try {
    await navigator.serviceWorker.register('/sw.js');
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    // a subscription made with another key (keys changed on the server) cannot be reused
    if (existing) await existing.unsubscribe();
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) as BufferSource });
    return { ok: true, subscription: sub.toJSON() };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

export type ForgetDeps = {
  subscription: () => Promise<PushSubscription | null>;
  call: (path: string, options?: { method?: 'GET' | 'DELETE' }) => Promise<unknown>;
};

/**
 * Logout: this browser stops getting push — its device is removed on the server (found by its endpoint) and the
 * browser's subscription is dropped. Never throws and gives up after `timeoutMs`, so logout always goes on.
 */
export async function forgetThisDevice(deps: ForgetDeps = { subscription: currentSubscription, call: (path, o) => api<unknown>(path, o) }, timeoutMs = 3000): Promise<void> {
  const work = (async () => {
    const sub = await deps.subscription().catch(() => null);
    if (!sub) return;
    try {
      const settings = (await deps.call('/api/me/push')) as PushSettings;
      const mine = settings.devices.find((d) => d.endpoint === sub.endpoint);
      if (mine) await deps.call(`/api/me/push/subscriptions/${encodeURIComponent(mine.id)}`, { method: 'DELETE' });
    } catch {
      /* the server forgets it anyway on the next 404/410 from the push service */
    }
    await sub.unsubscribe().catch(() => false);
  })();
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([work, new Promise<void>((resolve) => (timer = setTimeout(resolve, timeoutMs)))]);
  clearTimeout(timer);
}
